import { lazy, Suspense, useState } from "react";
import { Link, useParams } from "wouter";
import { useContainer, useRestartContainer, useStartContainer, useStopContainer } from "../../api/queries";
import { isNotFound, isUnauthorized } from "../../api/errors";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/Button";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { ContainerManagedPill, ContainerStatePill } from "../../components/StatePill";
import { TabPanel, Tabs } from "../../components/Tabs";
import { useToast } from "../../toast/ToastContext";
import { AdoptContainerModal } from "./AdoptContainerModal";
import { LiveLogPanel } from "./LiveLogPanel";
import "./container-detail.css";

// xterm.js is a sizable dependency (~150kB gzipped) only the terminal tab
// needs — code-split so it's fetched on first open rather than bundled into
// every page load.
const TerminalPanel = lazy(() => import("./TerminalPanel").then((m) => ({ default: m.TerminalPanel })));

const DETAIL_TABS = [
  { id: "logs", label: "Logs" },
  { id: "terminal", label: "Terminal" },
];

export function ContainerDetailPage() {
  const { nodeId, containerId } = useParams<{ nodeId: string; containerId: string }>();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const [activeTab, setActiveTab] = useState(DETAIL_TABS[0].id);

  const { data: container, isPending, isError, error, refetch } = useContainer(nodeId, containerId);
  const startMutation = useStartContainer();
  const stopMutation = useStopContainer();
  const restartMutation = useRestartContainer();
  const [adoptOpen, setAdoptOpen] = useState(false);

  const pendingAction =
    startMutation.isPending || stopMutation.isPending || restartMutation.isPending;

  function handleAction(kind: "start" | "stop" | "restart") {
    if (!containerId || !nodeId) return;
    const mutation = kind === "start" ? startMutation : kind === "stop" ? stopMutation : restartMutation;
    const verb = kind === "start" ? "started" : kind === "stop" ? "stopped" : "restarted";
    mutation.mutate(
      { nodeId, containerId },
      {
        onSuccess: () => pushToast({ title: `Container ${verb}.`, tone: "success" }),
        onError: (err) =>
          pushToast({
            title: `Couldn't ${kind} the container`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          }),
      },
    );
  }

  if (isPending) {
    return <LoadingState label="Loading container…" />;
  }

  if (isError) {
    if (isUnauthorized(error)) {
      return <LoadingState label="Loading container…" />;
    }
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Container not found"
          description="It may have been removed, or the link is out of date. Head back to the machine it was running on."
          action={
            <Link to={`/hosts/${nodeId}`} className="button button--secondary">
              Back to the machine
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this container."}
        onRetry={refetch}
      />
    );
  }

  if (!container) return null;

  // Mirrors the server's authz matrix (container lifecycle actions are
  // granted to operator/admin only, server/internal/authz/model.go) — this
  // only hides/disables the controls; the server enforces the real decision
  // on every request regardless of what this check says (S-8).
  const canManage = canManageFleet(session?.role);
  const roleHint = canManage ? undefined : "Viewers can't manage containers.";

  const canStart =
    canManage && (container.state === "exited" || container.state === "created" || container.state === "dead");
  const canStop =
    canManage && (container.state === "running" || container.state === "paused" || container.state === "restarting");
  const canRestart = canManage && (container.state === "running" || container.state === "paused");

  return (
    <div className="container-detail">
      {/* IA slice 6: the fleet-wide containers browser is retired — this container is only
          ever reached from its owning machine (Fleet) or service (Operate), so its one
          ancestor crumb goes to the machine it's running on, the one place both entry
          points share. */}
      <Breadcrumb items={[{ label: "Fleet", to: "/hosts" }, { label: "Machine", to: `/hosts/${container.nodeId}` }, { label: container.name }]} />

      <div className="container-detail__header">
        <div>
          <div className="container-detail__title-row">
            <h1 className="container-detail__title">{container.name}</h1>
            <ContainerStatePill state={container.state} />
            <ContainerManagedPill managed={container.managed} />
          </div>
          <p className="container-detail__image">{container.image}</p>
        </div>
        <div className="container-detail__actions">
          {!container.managed && canManage && (
            <Button variant="primary" onClick={() => setAdoptOpen(true)}>
              Adopt
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={!canStart || pendingAction}
            title={roleHint}
            onClick={() => handleAction("start")}
          >
            {startMutation.isPending ? "Starting…" : "Start"}
          </Button>
          <Button
            variant="secondary"
            disabled={!canRestart || pendingAction}
            title={roleHint}
            onClick={() => handleAction("restart")}
          >
            {restartMutation.isPending ? "Restarting…" : "Restart"}
          </Button>
          <Button
            variant="danger"
            disabled={!canStop || pendingAction}
            title={roleHint}
            onClick={() => handleAction("stop")}
          >
            {stopMutation.isPending ? "Stopping…" : "Stop"}
          </Button>
        </div>
      </div>

      <dl className="container-detail__meta">
        <div>
          <dt>Status</dt>
          <dd>{container.status}</dd>
        </div>
        <div>
          <dt>Node</dt>
          <dd>{container.nodeName}</dd>
        </div>
        <div>
          <dt>Service</dt>
          <dd>
            {container.service ? (
              // The Container → Service jump (#288): reach the owning service's detail —
              // placements, deployment history, and the path back to logs/exec/restart.
              <Link
                to={`/projects/${container.service.projectId}/services/${container.service.serviceId}`}
              >
                {container.service.serviceName}
              </Link>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>Ports</dt>
          <dd>
            {container.ports.length === 0
              ? "—"
              : container.ports
                  .map((p) => (p.hostPort ? `${p.hostPort}→${p.containerPort}/${p.protocol}` : `${p.containerPort}/${p.protocol}`))
                  .join(", ")}
          </dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{new Date(container.createdAt).toLocaleString()}</dd>
        </div>
      </dl>

      <Tabs items={DETAIL_TABS} activeId={activeTab} onChange={setActiveTab} ariaLabel="Container detail views" />

      <TabPanel id="logs" activeId={activeTab}>
        <LiveLogPanel nodeId={container.nodeId} containerId={container.id} />
      </TabPanel>

      <TabPanel id="terminal" activeId={activeTab}>
        <Suspense fallback={<LoadingState label="Loading terminal…" />}>
          <TerminalPanel nodeId={container.nodeId} containerId={container.id} />
        </Suspense>
      </TabPanel>

      {!container.managed && canManage && (
        <AdoptContainerModal container={adoptOpen ? container : null} onClose={() => setAdoptOpen(false)} />
      )}
    </div>
  );
}
